import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  getMenuByIdAPI,
  getTableOrdersAPI,
} from "../../assets/js/constants/APIURLs";
import {
  replaceMultipleTokensFromAPI,
  slugify,
} from "../../assets/js/constants/commonFunctions";
import {
  FAILEDSTATUS,
  IDLESTATUS,
  LOADINGSTATUS,
  PREMIUMPLAN,
  PROPLAN,
  secureSecretkey,
  SUCCESSSTATUS,
} from "../../assets/js/constants/constant";

export const fetchMenu = createAsyncThunk(
  "menu/fetchMenu",
  async (menuId, thunkAPI) => {
    try {
      const res = await axios.get(
        replaceMultipleTokensFromAPI(getMenuByIdAPI, { 1: menuId }),
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      const menu = await res.data.data;
      const tokenId = menu?.tokenId;
      const restaurantName = menu?.restaurantName;
      const restaurantLogo = menu?.restaurantLogo;
      const dishItems = (menu?.dishItems || []).filter(
        (item) => item.available !== false,
      );
      const subscribedPlan = menu?.subscribedPlan;
      const categories = getCategoriesWithCount(dishItems, subscribedPlan);
      const newCategories = menu?.newCategory;
      const menuStatus = menu?.status;
      const planExpired = menu?.planExpired;
      return {
        menuId,
        tokenId,
        restaurantName,
        restaurantLogo,
        dishItems,
        categories,
        newCategories,
        subscribedPlan,
        menuStatus,
        planExpired,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// Table Order and Billing Details
export const fetchTableOrders = createAsyncThunk(
  "menu/fetchTableOrders",
  async ({ menuId, tableNo }, thunkAPI) => {
    try {
      const res = await axios.get(
        replaceMultipleTokensFromAPI(getTableOrdersAPI, {
          1: menuId,
          2: tableNo,
        }),
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

function getCategoriesWithCount(items = [], subscribedPlan) {
  const map = {};
  let chefSpecialCount = 0;
  const isPremiumUser =
    subscribedPlan === PREMIUMPLAN || subscribedPlan === PROPLAN;
  items.forEach((item) => {
    if (isPremiumUser && item.chefSpecial) {
      chefSpecialCount++;
    }
    const cat = item.category || "Uncategorized";
    if (!map[cat]) {
      map[cat] = {
        categoryName: cat,
        count: 0,
        dataTarget: slugify(cat).toLowerCase(),
      };
    }
    map[cat].count += 1;
  });
  let categories = Object.values(map);
  categories.sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  if (isPremiumUser && chefSpecialCount > 0) {
    categories.unshift({
      categoryName: "Chef Special",
      count: chefSpecialCount,
      dataTarget: "chef-special",
      chefSpecialCategory: true,
    });
  }
  return categories;
}

function normalizeSelectedTypes(input) {
  const arr = Array.isArray(input) ? input : [input];
  const set = new Set();
  arr.forEach((raw) => {
    if (!raw) return;
    const t = String(raw).trim().toLowerCase();
    if (t === "all") {
      set.clear();
      set.add("all");
      return;
    }
    if (t === "veg" || t === "vegetarian") return set.add("veg");
    if (t === "non-veg" || t === "non veg" || t === "nonveg")
      return set.add("nonveg");
    if (t === "egg") return set.add("egg");
  });

  if (set.size === 0) set.add("all");
  return Array.from(set);
}

function filterByType(items, selectedTypes) {
  const types = normalizeSelectedTypes(selectedTypes);
  if (types.includes("all")) return items;
  return items.filter((item) => {
    const type = (item.type || "").toLowerCase();
    const isEgg = item.eggProduct === true;
    if (types.includes("egg") && isEgg) return true;
    if (types.includes("veg") && type === "veg") return true;
    if (
      types.includes("nonveg") &&
      (type === "non-veg" || type === "nonveg" || type === "non veg")
    )
      return true;
    return false;
  });
}

function filterItems(items, query) {
  const term = query.trim().toLowerCase();
  if (!term) return items;
  return items.filter((item) => (item.name || "").toLowerCase().includes(term));
}

const menuSlice = createSlice({
  name: "menu",
  initialState: {
    menuId: "",
    tokenId: "",
    restaurantName: "",
    restaurantLogo: "",
    subscribedPlan: "",
    originalDishItems: [],
    dishItems: [],
    categories: [],
    newCategories: [],
    menuStatus: "",
    planExpired: "",
    searchValue: "",
    selectedTypes: ["All"],
    filteredItems: [],
    filteredCategories: [],
    status: IDLESTATUS,
    error: null,
    tableOrderStatus: IDLESTATUS,
    tableOrder: {},
    billingDetails: {},
  },

  reducers: {
    setSearchValue(state, action) {
      state.searchValue = action.payload;
      let filtered = filterByType(state.originalDishItems, state.selectedTypes);
      filtered = filterItems(filtered, state.searchValue);
      state.filteredItems = filtered;
      state.dishItems = filtered;
      state.filteredCategories = getCategoriesWithCount(
        filtered,
        state.subscribedPlan,
      );
    },
    setFoodTypes(state, action) {
      state.selectedTypes = normalizeSelectedTypes(action.payload);
      let filtered = filterByType(state.originalDishItems, state.selectedTypes);
      filtered = filterItems(filtered, state.searchValue);
      state.filteredItems = filtered;
      state.dishItems = filtered;
      state.filteredCategories = getCategoriesWithCount(
        filtered,
        state.subscribedPlan,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenu.pending, (state) => {
        state.status = LOADINGSTATUS;
        state.error = null;
      })
      .addCase(fetchMenu.fulfilled, (state, action) => {
        state.status = SUCCESSSTATUS;
        state.menuId = action.payload.menuId;
        state.tokenId = action.payload.tokenId;
        state.restaurantName = action.payload.restaurantName;
        state.restaurantLogo = action.payload.restaurantLogo;
        state.originalDishItems = action.payload.dishItems;
        state.dishItems = action.payload.dishItems;
        state.filteredItems = action.payload.dishItems;
        state.categories = action.payload.categories;
        state.newCategories = action.payload.newCategories;
        state.filteredCategories = action.payload.categories;
        state.menuStatus = action.payload.menuStatus;
        state.planExpired = action.payload.planExpired;
        state.subscribedPlan = action.payload.subscribedPlan;
      })
      .addCase(fetchMenu.rejected, (state, action) => {
        state.status = FAILEDSTATUS;
        state.error = action.payload || action.error.message;
      })

      // Table Order and Billing Details
      .addCase(fetchTableOrders.pending, (state) => {
        state.tableOrderStatus = LOADINGSTATUS;
      })
      .addCase(fetchTableOrders.fulfilled, (state, action) => {
        state.tableOrderStatus = SUCCESSSTATUS;
        state.tableOrder = action.payload.tableOrder;
        state.billingDetails = action.payload.billingDetails;
      })
      .addCase(fetchTableOrders.rejected, (state) => {
        state.tableOrderStatus = FAILEDSTATUS;
      });
  },
});

export const { setSearchValue, setFoodTypes } = menuSlice.actions;
export default menuSlice.reducer;
